<script setup lang="ts">
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAttachmentDelete } from '@satellite/composables/useAttachmentDelete';

import type { DatabaseEntities, Maybe, YardTaskAttachment } from '@nova/core';

defineOptions({ name: 'AttachmentDeleteDialog' });

const {
  relatedEntity,
  relatedId,
  title,
  message,
  confirmLabel,
  cancelLabel,
  testId = 'attachment'
} = defineProps<{
  /** Which entity the documents hang off, e.g. `DatabaseEntities.YardTask`. */
  relatedEntity: DatabaseEntities;
  /** The entity's id. Nothing is removed until there is one. */
  relatedId: Maybe<string>;
  /** Heading of the confirmation, e.g. "Delete this attachment?". */
  title: string;
  /** What confirming will do, e.g. that it cannot be undone. */
  message: string;
  /** Label of the destructive action, e.g. "Delete". */
  confirmLabel: string;
  /** Label of the dismissal, e.g. "Nevermind". */
  cancelLabel: string;
  /** Prefix for every `data-testid` this dialog renders, e.g. `<testId>-delete-dialog`. */
  testId?: string;
}>();

/** The attachment to remove. Setting it opens the confirmation; clearing it closes. */
const attachment = defineModel<Maybe<YardTaskAttachment>>({ default: null });

/**
 * Read-only for the caller: the removal is this dialog's, but the tiles behind it disable their
 * own remove affordance while one is in flight, and only the caller can reach them.
 */
const isDeleting = defineModel<boolean>('isDeleting', { default: false });

const emit = defineEmits<{
  deleted: [];
  error: [error: unknown];
}>();

const { t } = useI18n();

const { deleteAttachment, isDeleting: isRequestPending } = useAttachmentDelete({
  relatedEntity: () => relatedEntity,
  relatedId: () => relatedId,
  onSuccess: () => emit('deleted'),
  onError: error => emit('error', error)
});

watch(isRequestPending, pending => {
  isDeleting.value = pending;
});

const isOpen = computed({
  get: () => Boolean(attachment.value),
  set: (value: boolean) => {
    if (!value) {
      attachment.value = null;
    }
  }
});

/** Closes on both outcomes: a failure is reported through `error`, not by staying open. */
async function confirmDelete(): Promise<void> {
  const target = attachment.value;
  if (!target) {
    return;
  }

  await deleteAttachment(target.id);
  attachment.value = null;
}
</script>

<template>
  <v-dialog
    v-model="isOpen"
    max-width="420"
    :persistent="isRequestPending"
    :aria-label="title"
    @keydown.escape="isRequestPending || (isOpen = false)">
    <v-card :data-testid="`${testId}-delete-dialog`">
      <v-card-title class="d-flex align-center ga-2 pa-4">
        <v-icon icon="mdi-alert" color="error" size="20" />
        <h4 class="typography-heading-md-bold flex-grow-1">{{ title }}</h4>
        <v-btn
          variant="text"
          icon="mdi-close"
          size="small"
          density="comfortable"
          :disabled="isRequestPending"
          :aria-label="t('global.basicTerms.close')"
          :data-testid="`${testId}-delete-dismiss`"
          @click="isOpen = false" />
      </v-card-title>

      <v-card-text class="typography-body-md text--color-text-secondary px-4 pb-6">
        {{ message }}
      </v-card-text>

      <v-card-actions class="d-flex justify-end pa-4">
        <v-btn
          variant="outlined"
          size="large"
          class="typography-button-lg"
          :disabled="isRequestPending"
          :data-testid="`${testId}-delete-cancel`"
          @click="isOpen = false">
          {{ cancelLabel }}
        </v-btn>
        <v-btn
          variant="elevated"
          color="error"
          size="large"
          class="typography-button-lg"
          :loading="isRequestPending"
          :data-testid="`${testId}-delete-confirm`"
          @click="confirmDelete">
          {{ confirmLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
